// ---------------------------------------------------------------------------
// BBR -> Energy-consultant building use mapping
//
// Source: Energiconsulenternes forventede elforbrug pr. m² pr. år
//         (excl. el til opvarmning)
// ---------------------------------------------------------------------------

/** Energy-consultant building use categories used for benchmarking. */
export type EnergyconsultantBuildingUseType =
  | 'Parcelhuse'
  | 'Række kæde dobbelthuse'
  | 'Etageboliger'
  | 'Sociale institutioner'
  | 'Daginstitution'
  | 'Transportvirksomhed'
  | 'Liberalt erhverv'
  | 'Offentlig administration'
  | 'Hotel'
  | 'Restauranter'
  | 'Handel og service'
  | 'Supermarked'
  | 'Forlystelser kultur sport'
  | 'Museer, biblioteker, udstillinger mv.'
  | 'Sportsanlæg'
  | 'Undervisning'
  | 'Hospitaler'
  | 'Døgninstitutioner';

/**
 * Expected electricity consumption per m² per year, excluding electricity
 * used for heating.  Values are from the energy-consultant reference table.
 */
export const ENERGYCONSULTANT_EXPECTED_CONSUMPTION_KWH_M2: Record<
  EnergyconsultantBuildingUseType,
  number
> = {
  Parcelhuse: 35,
  'Række kæde dobbelthuse': 41,
  Etageboliger: 38,
  'Sociale institutioner': 66,
  Daginstitution: 37,
  Transportvirksomhed: 206,
  'Liberalt erhverv': 49,
  'Offentlig administration': 49,
  Hotel: 51,
  Restauranter: 473,
  'Handel og service': 112,
  Supermarked: 334,
  'Forlystelser kultur sport': 82,
  'Museer, biblioteker, udstillinger mv.': 52,
  Sportsanlæg: 67,
  Undervisning: 46,
  Hospitaler: 93,
  Døgninstitutioner: 48,
};

/**
 * Maps BBR building use category labels (as they appear in BuildingUsesWithArea)
 * to the closest energy-consultant building use type.
 *
 * `null` means no meaningful benchmark reference exists for that BBR category
 * (e.g. outbuildings, agriculture, garages).
 *
 * Notes on ambiguous categories:
 * - "Bolig"                  -> Etageboliger (38) used as a generic residential default.
 * - "Hotel og restauration"  -> Hotel (51) used as default; restaurants have significantly
 *                              higher consumption (473 kWh/m²/year) and should be
 *                              classified separately when possible.
 * - "Sundhedsformål"         -> Hospitaler (93); smaller clinics may be closer to
 *                              Sociale institutioner (66).
 * - "Kontor"                 -> Liberalt erhverv (49); public-sector offices may be
 *                              better mapped to Offentlig administration (49).
 * - "Kultur og fritid"       -> Forlystelser kultur sport (82); museums/libraries
 *                              within this group are closer to 52 kWh/m²/year.
 */
export const BBR_TO_ENERGYCONSULTANT_BUILDING_USE: Record<
  string,
  EnergyconsultantBuildingUseType | null
> = {
  // ── Residential ──────────────────────────────────────────────────────────
  Bolig: 'Etageboliger', // generic residential default
  'Sommerhus, fritidshus og kolonihave': 'Parcelhuse',
  Omsorgsbolig: 'Døgninstitutioner',

  // ── Commercial / office ──────────────────────────────────────────────────
  Kontor: 'Liberalt erhverv',
  Detailhandel: 'Handel og service',

  // ── Hospitality ──────────────────────────────────────────────────────────
  'Hotel og restauration': 'Hotel', // see note above

  // ── Industry / logistics ─────────────────────────────────────────────────
  'Transport og logistik': 'Transportvirksomhed',
  Industri: null, // no close reference value
  Lager: null, // warehouses not in reference table

  // ── Education & care ─────────────────────────────────────────────────────
  Uddannelsesinstitutioner: 'Undervisning',
  Daginstitution: 'Daginstitution',
  'Andre Institutionsformål': 'Sociale institutioner',

  // ── Health ───────────────────────────────────────────────────────────────
  Sundhedsformål: 'Hospitaler', // see note above

  // ── Culture, sport & leisure ─────────────────────────────────────────────
  'Kultur og fritid': 'Forlystelser kultur sport', // see note above
  Idræt: 'Sportsanlæg',
  Svømmehal: 'Sportsanlæg',
  Forlystelsespark: 'Forlystelser kultur sport',

  // ── Agriculture ──────────────────────────────────────────────────────────
  Landbrug: null,
  'Tiloversbleven landbrugsbygning': null,
  Drivhus: null,
  'Bygning til træning og opstaldning af heste': null,

  // ── Energy & utilities ───────────────────────────────────────────────────
  'Energi og forsyning': null,

  // ── Miscellaneous / no benchmark ─────────────────────────────────────────
  'Ukendt bygning': null,
  Udhus: null,
  Garage: null,
  Carport: null,
  'Fritliggende udestue': null,
  'Fritliggende overdækning': null,
  'Faldefærdig bygning': null,
  Beskyttelsesrum: null,
};

/**
 * Looks up the expected electricity consumption (kWh/m²/year, excl. heating)
 * for a given BBR building use category label.
 *
 * Returns `null` when no benchmark reference exists for that category.
 */
export function getExpectedConsumptionForBbrCategory(
  bbrCategory: string,
): number | null {
  const consultantType = BBR_TO_ENERGYCONSULTANT_BUILDING_USE[bbrCategory];
  if (consultantType == null) return null;
  return ENERGYCONSULTANT_EXPECTED_CONSUMPTION_KWH_M2[consultantType];
}

/**
 * Looks up the energy-consultant building use type for a given BBR category label.
 * Returns `null` when no mapping exists.
 */
export function getEnergyconsultantTypeForBbrCategory(
  bbrCategory: string,
): EnergyconsultantBuildingUseType | null {
  return BBR_TO_ENERGYCONSULTANT_BUILDING_USE[bbrCategory] ?? null;
}
